import { ChangeEvent, FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { UpdateType } from "@/enum/list.enum";
import { useUpdateListMutation } from "@/pages/Private/redux/list/list.api";

import { LoadingOverlay, Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";

import { ButtonColor } from "@/components/Button/types";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { runOrToast } from "@/utils/runOrToast";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { GetAllSendersUsersResponse } from "@/types/admin";

import { PendingSchedulesDialog } from "@/pages/Private/pages/Account/components/PendingSchedulesDialog";

import { CampaignAction } from "@/pages/Private/pages/Account/schema/pendingSchedules";

import { StoredCampaignEntry } from "../utils/campaignRevision.util";

import { Schedule } from "./Schedule";
import { Sender } from "./Sender";
import { List } from "../../../schema/list";

import { useCampaignSenderRemoval } from "../../../hooks/useCampaignSenderRemoval";

import { CampaignScheduleFields } from "./CampaignScheduleFields";
import { CampaignSettingsSection } from "./CampaignSettingsSection";
import { DeleteCampaignModal } from "./DeleteCampaignModal";
import { CampaignTrackingOptions } from "./CampaignTrackingOptions";
import { SameCompanyStopOption } from "./SameCompanyStopOption";
import { SenderSelector } from "./SenderSelector/SenderSelector";
import { getCampaignStepChannels } from "./SenderSelector/senderSelector.helpers";
import { SenderCountBadge } from "./SenderCountBadge";
import { computeCurrentSenders } from "../utils/computeCurrentSenders.util";
import { countSelectedSenderUsers } from "../utils/countSelectedSenderUsers.util";
import { DEFAULT_CAMPAIGN_SCHEDULE } from "../utils/defaultCampaignSchedule";
import { writeCampaignSettings } from "../utils/writeCampaignSettings.util";

export type EditCampaignModalProps = {
	isOpened: boolean;
	/**
	 * Dismissed without writing anything. The builder pauses its autosave while this modal is open
	 * — the modal rewrites the whole campaign entry — so the two exits are not interchangeable:
	 * this one hands the record straight back, `onSaved` makes the canvas re-baseline first
	 * (PREC-4151).
	 */
	handleClose: () => void;
	/** Accepted settings entry; omitted only when the campaign was deleted. */
	onSaved: (entry?: StoredCampaignEntry) => void;
	listData: List;
	setCurrentlySelectedCampaign: (value: string, campaignUuid: string) => void;
	currentCampaign: string;
	currentCampaignUuid: string;
};

type EditCampaignFormProps = EditCampaignModalProps & {
	sendingUsers: GetAllSendersUsersResponse["data"];
};

const EditCampaignForm: FunctionComponent<EditCampaignFormProps> = ({
	isOpened,
	handleClose,
	onSaved,
	listData,
	setCurrentlySelectedCampaign,
	currentCampaign,
	currentCampaignUuid,
	sendingUsers,
}) => {
	const { t } = useTranslation();

	const { run, isRunning } = useSingleFlight();
	const pendingRef = useRef(false);
	const campaignConfig = listData?.campaignConfig?.[currentCampaignUuid];

	const [newCampaignName, setNewCampaignName] = useState<string>(currentCampaign);

	const [pausePeopleFromTheSameCompany, setPausePeopleFromTheSameCompany] = useState<boolean>(
		campaignConfig?.pausePeopleFromTheSameCompany || false
	);
	const [trackClicks, setTrackClicks] = useState<boolean>(campaignConfig?.trackClicks || false);
	const [trackOpens, setTrackOpens] = useState<boolean>(campaignConfig?.trackOpens || false);
	const [stopCampaignAt, setStopCampaignAt] = useState<Date | null>(
		campaignConfig?.stopCampaignAt ? new Date(campaignConfig.stopCampaignAt) : null
	);

	const [timezone, setTimezone] = useState<string>(campaignConfig?.timezone || "");

	const [schedule, setSchedule] = useState<Schedule>(
		campaignConfig?.schedule || DEFAULT_CAMPAIGN_SCHEDULE
	);

	// Language targeting (PREC-3564): the editor was removed, but any value already saved on the
	// campaign is preserved so existing language-based auto-assignment keeps working untouched.
	const languages: string[] = campaignConfig?.languages || [];

	// Channels the campaign's saved sequence needs — drives greying out sending users that can't
	// perform a step the campaign already contains.
	const steps = useMemo(
		() => getCampaignStepChannels(campaignConfig?.initialNodes),
		[campaignConfig?.initialNodes]
	);

	const currentSenders = useMemo<Sender[]>(() => {
		return computeCurrentSenders(sendingUsers, campaignConfig?.senders);
	}, [sendingUsers, campaignConfig]);

	const [senders, setSenders] = useState<Sender[]>(currentSenders);

	// "X/Y" badge on the Senders section — distinct users with an active sender / total users.
	const senderSelection = useMemo(() => countSelectedSenderUsers(senders), [senders]);

	const errors = useMemo(() => {
		const errors: Record<string, string> = {};

		if (!newCampaignName) {
			errors.newCampaignName = "Campaign name is required";
		}

		if (!timezone) {
			errors.timezone = "Timezone is required";
		}

		if (Object.values(schedule).every((day) => !day.active)) {
			errors.schedule = "At least one day of the week must be active";
		}

		if (senders.length === 0) {
			errors.senders = "At least one sender is required";
		}

		if (Object.values(senders).every((sender) => !sender.active)) {
			errors.senders = "At least one sender must be active";
		}

		return errors;
	}, [newCampaignName, timezone, schedule, senders]);

	const formErrors = useMemo(() => {
		const formErrors: ERROR_TYPE[] = [];

		if (!newCampaignName) {
			formErrors.push(ERROR_TYPE.REQUIRED);
		}

		if (!timezone) {
			formErrors.push(ERROR_TYPE.REQUIRED);
		}

		if (Object.values(schedule).every((day) => !day.active)) {
			formErrors.push(ERROR_TYPE.REQUIRED);
		}

		// Missing senders deliberately do NOT block saving (PREC-3827): accounts without a
		// connected sender can still edit the campaign — it is persisted as invalid instead.
		return formErrors;
	}, [newCampaignName, timezone, schedule]);

	const [updateList, { isLoading: isUpdating }] = useUpdateListMutation();

	const saveCampaignConfig = useCallback(async () => {
		// Removing the last active sender must immediately downgrade the campaign: the backend
		// enrolls into a campaign only while it is stored with `isValid: true` (a missing flag means
		// invalid), and the sequence builder only recomputes validity on its next mount.
		const hasActiveSender = senders.some((sender) => sender.active);

		// A refused save used to escape as an unhandled rejection and Save did nothing (PREC-4390).
		const saved = await runOrToast(async () => {
			const response = await writeCampaignSettings({
				campaignUuid: currentCampaignUuid,
				storedEntry: listData?.campaignConfig?.[currentCampaignUuid],
				settings: {
					name: newCampaignName,
					languages,
					isEventBase: false,
					eventAt: null,
					pausePeopleFromTheSameCompany,
					trackClicks,
					trackOpens,
					stopCampaignAt,
					timezone,
					schedule,
					senders,
					...(hasActiveSender ? {} : { isValid: false }),
				},
				write: (entry) =>
					updateList({
						id: +(listData?.id || 0),
						updateType: UpdateType.SETTINGS,
						campaignConfig: JSON.stringify({
							...listData?.campaignConfig,
							[currentCampaignUuid]: entry,
						}),
						campaignIdToUpdate: currentCampaignUuid,
						campaignWriteOrigin: "settings",
					}).unwrap(),
			});

			onSaved(response.campaignConfig?.[currentCampaignUuid]);
		}, t("errors.saveFailed"));

		if (!saved) {
			return;
		}

		setNewCampaignName(newCampaignName);
	}, [
		updateList,
		listData,
		currentCampaignUuid,
		newCampaignName,
		languages,
		pausePeopleFromTheSameCompany,
		trackClicks,
		trackOpens,
		stopCampaignAt,
		timezone,
		schedule,
		senders,
		onSaved,
		t,
	]);

	const {
		checkForPendingSchedules,
		confirmReassignment,
		cancelReassignment,
		showPendingDialog,
		totalCount: pendingTotalCount,
		campaigns: pendingCampaigns,
		isLoading: isCheckingPending,
	} = useCampaignSenderRemoval({
		listId: +(listData?.id || 0),
		campaignId: currentCampaignUuid,
		currentSenders,
		newSenders: senders,
		onReassignComplete: saveCampaignConfig,
	});

	const handleEditCampaign = () =>
		run(async () => {
			pendingRef.current = true;
			try {
				if (getAllErrors(undefined, formErrors).length > 0) {
					return;
				}

				const canProceed = await checkForPendingSchedules();

				if (canProceed) {
					await saveCampaignConfig();
				}
			} finally {
				pendingRef.current = false;
			}
		});

	const handleConfirmReassignment = useCallback(
		(campaignActions: CampaignAction[]) =>
			run(async () => {
				pendingRef.current = true;
				try {
					await confirmReassignment(campaignActions);
				} finally {
					pendingRef.current = false;
				}
			}),
		[confirmReassignment, run]
	);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

	// The delete runs in DeleteCampaignModal, which says how many leads are assigned and what happens
	// to them before anything is removed (PREC-4258); afterwards the builder moves to another campaign.
	const handleCampaignDeleted = () => {
		setShowDeleteModal(false);

		const campaigns = Object.keys(listData?.campaignConfig || {}).filter(
			(campaign) => campaign !== currentCampaignUuid && campaign !== "name"
		);

		setCurrentlySelectedCampaign(
			listData?.campaignConfig?.[campaigns[0]]?.name || "",
			campaigns[0] || ""
		);
		onSaved();
		setNewCampaignName("");
	};

	return (
		<>
			<Modal
				disableSave={getAllErrors(undefined, formErrors).length > 0}
				handleClose={() => {
					if (!pendingRef.current) {
						handleClose();
					}
				}}
				handleSave={handleEditCampaign}
				handleSecondSave={() => {
					if (!pendingRef.current) {
						setShowDeleteModal(true);
					}
				}}
				isLoading={isRunning || isUpdating || isCheckingPending}
				isOpened={isOpened}
				secondSubmitButtonColor={ButtonColor.DANGER}
				secondSubmitButtonText="Delete Campaign"
				size="md"
				title="Edit Campaign"
			>
				<div className="flex flex-col gap-3">
					<CampaignSettingsSection
						status={errors.newCampaignName ? "missing" : "done"}
						title="Campaign"
					>
						<div className="flex flex-col gap-4">
							<InputField
								error={errors.newCampaignName}
								handleChange={(e: ChangeEvent<HTMLInputElement>) =>
									setNewCampaignName(e.target.value)
								}
								label="Campaign Name"
								minLength={5}
								name="campaignName"
								placeholder="Campaign Name"
								required={true}
								showError={!!errors.newCampaignName}
								value={newCampaignName}
							/>
							<SameCompanyStopOption
								isChecked={pausePeopleFromTheSameCompany}
								onChange={setPausePeopleFromTheSameCompany}
							/>
							<CampaignTrackingOptions
								setTrackClicks={setTrackClicks}
								setTrackOpens={setTrackOpens}
								trackClicks={trackClicks}
								trackOpens={trackOpens}
							/>
							<InputField
								handleChange={(e: ChangeEvent<HTMLInputElement>) => {
									if (e.target.value) {
										setStopCampaignAt(new Date(e.target.value));
									} else {
										setStopCampaignAt(null);
									}
								}}
								information="Automatically stop this campaign on the selected date"
								label="Stop Campaign At"
								min={format(new Date(), "yyyy-MM-dd")}
								name="stopCampaignAt"
								type="date"
								value={stopCampaignAt ? format(stopCampaignAt, "yyyy-MM-dd") : ""}
							/>
						</div>
					</CampaignSettingsSection>

					<CampaignSettingsSection
						status={errors.timezone || errors.schedule ? "missing" : "done"}
						title="Schedule"
					>
						<CampaignScheduleFields
							schedule={schedule}
							scheduleError={errors.schedule}
							setSchedule={setSchedule}
							setTimezone={setTimezone}
							timezone={timezone}
							timezoneError={!!errors.timezone}
						/>
					</CampaignSettingsSection>

					<CampaignSettingsSection
						headerRight={
							<SenderCountBadge
								active={senderSelection.selected > 0}
								selected={senderSelection.selected}
								total={senderSelection.total}
							/>
						}
						status={errors.senders ? "missing" : "done"}
						title="Senders"
					>
						<SenderSelector
							error={!!errors.senders}
							senders={senders}
							sendingUsers={sendingUsers}
							setSenders={setSenders}
							steps={steps}
						/>
						{errors.senders && (
							<>
								<div className="text-status-error text-sm mt-2">{errors.senders}</div>
								<div className="text-text-meta text-sm mt-1">
									You can still save this campaign — it will remain invalid and cannot start until a
									sender is connected and activated.
								</div>
							</>
						)}
					</CampaignSettingsSection>

					{getAllErrors(undefined, formErrors).length
						? renderErrorMessages(getAllErrors(undefined, formErrors))
						: null}
				</div>
			</Modal>
			{showPendingDialog && (
				<PendingSchedulesDialog
					campaigns={pendingCampaigns}
					isLoading={isCheckingPending}
					isOpen={showPendingDialog}
					senderType="email"
					totalCount={pendingTotalCount}
					zIndex={15001}
					onClose={cancelReassignment}
					onConfirm={handleConfirmReassignment}
				/>
			)}
			{showDeleteModal && (
				<DeleteCampaignModal
					campaignName={currentCampaign}
					campaignUuid={currentCampaignUuid}
					listId={+(listData?.id || 0)}
					zIndex={15001}
					onClose={() => setShowDeleteModal(false)}
					onDeleted={handleCampaignDeleted}
				/>
			)}
		</>
	);
};

/**
 * The gear's dialog. Its form seeds every field from the stored campaign once, when it mounts, and
 * never reads the props again — so it is mounted per opening and per campaign (`CampaignOverview`
 * renders it only while open, keyed by the campaign) and only once the sending users are known,
 * because the sender selection is built from them. Mounted for a whole builder session it saved the
 * campaign the builder was opened on over whichever one was open by then (PREC-4432).
 */
export const EditCampaignModal: FunctionComponent<EditCampaignModalProps> = (props) => {
	const { data: sendingUsers } = useGetSendersUsersQuery();

	// Only the first load waits: a background refetch must not unmount the form and its edits.
	if (!sendingUsers) {
		return <LoadingOverlay />;
	}

	return <EditCampaignForm {...props} sendingUsers={sendingUsers.data} />;
};
